import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Clock, 
  Lightbulb, 
  Rocket,
  Shield,
  Mail,
  Users,
  CreditCard,
  Bell,
  Download,
  UserX,
  MessageSquare,
  Palette,
  Globe,
  Smartphone,
  Zap,
  Lock,
  BarChart3,
  Calendar,
  FileText,
  Gift,
  Search,
  Folder,
  Star,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function RoadmapPage() {
  const navigate = useNavigate();

  const completedFeatures = [
    {
      title: "Secure Email System",
      description: "End-to-end encrypted email within the MyPatriotMail network",
      icon: Mail,
      date: "Mar 2024"
    },
    {
      title: "User Authentication",
      description: "Secure login, registration, and password management",
      icon: Lock,
      date: "Mar 2024"
    },
    {
      title: "Admin Dashboard",
      description: "Comprehensive admin tools for user and system management",
      icon: Shield,
      date: "May 2024"
    },
    {
      title: "Mobile Responsive",
      description: "Full functionality on mobile devices",
      icon: Smartphone,
      date: "Jun 2024"
    },
    {
      title: "Labels & Organization",
      description: "Create custom labels to organize your emails",
      icon: Folder,
      date: "Aug 2024"
    },
    {
      title: "Search Functionality",
      description: "Powerful email search with filters",
      icon: Search,
      date: "Sep 2024"
    },
    {
      title: "Contact Management",
      description: "Save and organize your email contacts",
      icon: Users,
      date: "Nov 2024"
    },
    {
      title: "Subscription Plans & Billing",
      description: "Multiple subscription tiers with Stripe integration",
      icon: CreditCard,
      date: "Jan 2025"
    },
    {
      title: "Email Signatures",
      description: "Custom email signatures with referral links",
      icon: FileText,
      date: "Mar 2025"
    },
    {
      title: "Theme Customization",
      description: "Choose your accent color and dark/light mode",
      icon: Palette,
      date: "May 2025"
    },
    {
      title: "Real-time Messenger",
      description: "Instant messaging with other MyPatriotMail users",
      icon: MessageSquare,
      date: "Jun 2025"
    },
    {
      title: "PWA Support",
      description: "Install as an app on your device",
      icon: Zap,
      date: "Aug 2025"
    },
    {
      title: "Referral Program",
      description: "Earn rewards by inviting friends - 10 referrals = 1 free year, 50 = lifetime",
      icon: Gift,
      date: "Sep 2025"
    },
    {
      title: "Data Export",
      description: "Download all your data in CSV or JSON format",
      icon: Download,
      date: "Oct 2025"
    },
    {
      title: "Cloud Storage",
      description: "Secure file storage powered by iDrive E2",
      icon: Globe,
      date: "Dec 2025"
    },
    {
      title: "Coupon System",
      description: "Discount codes and promotional offers at checkout",
      icon: Star,
      date: "Jan 2026"
    },
    {
      title: "Public Contact Form",
      description: "Contact support directly from the website",
      icon: Send,
      date: "Jan 2026"
    },
    {
      title: "Account Deletion",
      description: "Permanently delete your account and all data",
      icon: UserX,
      date: "Jan 2026"
    },
    {
      title: "Forgot Password",
      description: "Secure password reset via email link",
      icon: Lock,
      date: "Feb 2026"
    },
    {
      title: "My Patriot Wall",
      description: "Personal blog wall with media uploads, followers, and subscriptions",
      icon: Rocket,
      date: "Feb 2026"
    }
  ];

  const upcomingFeatures = [
    {
      title: "Custom Folder Limits",
      description: "Folder limits based on subscription tier",
      icon: Folder,
      status: "In Development",
      quarter: "Q1 2026"
    },
    {
      title: "Subscription Management UI",
      description: "Upgrade, downgrade, and view invoice history",
      icon: CreditCard,
      status: "Planned",
      quarter: "Q1 2026"
    },
    {
      title: "Email Notifications",
      description: "Get notified when admin replies to support tickets",
      icon: Bell,
      status: "Planned",
      quarter: "Q1 2026"
    },
    {
      title: "Quiet Hours",
      description: "Set notification-free periods",
      icon: Clock,
      status: "Planned",
      quarter: "Q2 2026"
    },
    {
      title: "Scheduled Emails",
      description: "Write now, send later",
      icon: Calendar,
      status: "Planned",
      quarter: "Q2 2026"
    },
    {
      title: "Email Analytics",
      description: "Track open rates for broadcasts",
      icon: BarChart3,
      status: "Planned",
      quarter: "Q2 2026"
    },
    {
      title: "GDPR/CCPA Compliance Page",
      description: "Full regulatory compliance documentation",
      icon: Globe,
      status: "Planned",
      quarter: "Q2 2026"
    }
  ];

  const futureIdeas = [
    {
      title: "Two-Factor Authentication",
      description: "Extra security layer for your account",
      icon: Shield
    },
    {
      title: "Email Templates",
      description: "Save and reuse email templates",
      icon: FileText
    },
    {
      title: "Calendar Integration",
      description: "Built-in calendar synced with emails",
      icon: Calendar
    },
    {
      title: "Video Conferencing",
      description: "Secure video calls within the platform",
      icon: Users
    },
    {
      title: "API Access",
      description: "Developer API for integrations",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-[#0d0d1a] text-white">
      {/* Header */}
      <header className="border-b border-[#2d2d44]/50 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <img 
              src="/mpm-logo.png" 
              alt="MyPatriotMail" 
              className="w-8 h-8 object-contain"
            />
            <span className="text-lg font-bold">MyPatriotMail</span>
          </div>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#6D4AFF]/20 rounded-full border border-[#6D4AFF]/30 mb-6">
            <Rocket className="w-4 h-4 text-[#6D4AFF]" />
            <span className="text-sm text-[#6D4AFF]">Product Roadmap</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Building the Future of
            <span className="text-[#6D4AFF]"> Secure Email</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            See what we've built, what's coming next, and help shape the future of MyPatriotMail
          </p>
        </div>
      </section>

      {/* Completed Features */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Completed Features</h2>
              <p className="text-gray-400 text-sm">{completedFeatures.length} features shipped</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedFeatures.map((feature, index) => (
              <div
                key={index}
                className="p-4 bg-[#1a1a2e] border border-[#2d2d44] rounded-xl hover:border-green-500/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-white truncate">{feature.title}</h3>
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{feature.description}</p>
                    <p className="text-xs text-green-500/70 mt-2">{feature.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Features */}
      <section className="py-12 px-4 bg-[#1a1a2e]/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#6D4AFF]/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#6D4AFF]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Coming Soon</h2>
              <p className="text-gray-400 text-sm">Features in development</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {upcomingFeatures.map((feature, index) => (
              <div
                key={index}
                className="p-5 bg-[#1a1a2e] border border-[#2d2d44] rounded-xl hover:border-[#6D4AFF]/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#6D4AFF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[#6D4AFF]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-semibold text-white">{feature.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        feature.status === 'In Development' 
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                          : 'bg-[#6D4AFF]/20 text-[#6D4AFF] border border-[#6D4AFF]/30'
                      }`}>
                        {feature.status}
                      </span>
                      <span className="text-xs text-gray-500">{feature.quarter}</span>
                    </div>
                    <p className="text-gray-400 mt-2">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Suggestions Placeholder */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Community Suggestions</h2>
              <p className="text-gray-400 text-sm">Your ideas shape our roadmap</p>
            </div>
          </div>
          
          <div className="p-8 bg-gradient-to-br from-amber-500/10 to-[#6D4AFF]/10 border border-amber-500/20 rounded-2xl text-center">
            <Lightbulb className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Have a Feature Idea?</h3>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">
              We're building MyPatriotMail for you. Share your ideas and help us prioritize what matters most to our community.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Your Idea
            </Button>
          </div>

          {/* Placeholder slots for future user suggestions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            <div className="p-5 border-2 border-dashed border-[#2d2d44] rounded-xl text-center">
              <div className="w-10 h-10 bg-[#2d2d44] rounded-lg flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-sm text-gray-500">Your idea here</p>
            </div>
            <div className="p-5 border-2 border-dashed border-[#2d2d44] rounded-xl text-center">
              <div className="w-10 h-10 bg-[#2d2d44] rounded-lg flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-sm text-gray-500">Your idea here</p>
            </div>
            <div className="p-5 border-2 border-dashed border-[#2d2d44] rounded-xl text-center">
              <div className="w-10 h-10 bg-[#2d2d44] rounded-lg flex items-center justify-center mx-auto mb-3">
                <Lightbulb className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-sm text-gray-500">Your idea here</p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-12 px-4 bg-[#1a1a2e]/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Rocket className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Future Vision</h2>
              <p className="text-gray-400 text-sm">Long-term possibilities we're exploring</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {futureIdeas.map((idea, index) => (
              <div
                key={index}
                className="p-4 bg-[#1a1a2e] border border-[#2d2d44] rounded-xl opacity-75 hover:opacity-100 transition-opacity"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <idea.icon className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{idea.title}</h3>
                    <p className="text-sm text-gray-400 mt-1">{idea.description}</p>
                    <p className="text-xs text-purple-400/50 mt-2">Exploring</p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* More placeholder slots */}
            <div className="p-4 border-2 border-dashed border-[#2d2d44] rounded-xl text-center flex items-center justify-center min-h-[100px]">
              <p className="text-sm text-gray-500">More coming based on your feedback</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2d2d44]/50 py-8 px-4 mt-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="/mpm-logo.png" 
              alt="MyPatriotMail" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold">MyPatriotMail</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-500">
            <button onClick={() => navigate('/privacy')} className="hover:text-[#6D4AFF]">Privacy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-[#6D4AFF]">Terms</button>
            <button onClick={() => navigate('/')} className="hover:text-[#6D4AFF]">Home</button>
          </div>
          <p className="text-gray-600 text-sm">© 2024-2026 MyPatriotMail</p>
        </div>
      </footer>
    </div>
  );
}
